import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getTenantSlugCookie } from '../utils/cookies';
import toast from 'react-hot-toast';
import { OffertifyLogo } from '../components/OffertifyLogo';
import { EnvironmentBanner } from '../components/EnvironmentBanner';

const Login: React.FC = () => {
  const { login, isAuthenticated, isLoading, user, tenant } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    tenant_slug: getTenantSlugCookie(), // Prefilled from cookie; empty = Host
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Show success message after login (must be before any conditional returns)
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      if (user?.is_host) {
        toast.success('Benvenuto Amministratore di Piattaforma!');
      } else if (tenant) {
        toast.success(`Benvenuto in ${tenant.name}!`);
      } else {
        toast.success('Login effettuato con successo!');
      }
    }
  }, [isAuthenticated, isLoading, user, tenant]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await login(formData);
      // Don't show toast here - it will be shown in the effect above after state updates
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Errore durante il login');
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <EnvironmentBanner className="fixed inset-x-0 top-0 z-50" />
      <div className="max-w-md w-full">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="mx-auto h-20 w-20 flex items-center justify-center rounded-2xl bg-primary-50 shadow-sm border border-primary-100">
            <OffertifyLogo className="h-12 w-12 text-primary-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-secondary-900">
            Accedi a Offertify
          </h2>
          <p className="mt-2 text-sm text-secondary-600">
            Gestione offerte commerciali per la tua azienda
          </p>
        </div>
        
        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-lg border border-secondary-200 p-8">
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="tenant_slug" className="block text-sm font-medium text-secondary-700 mb-1">
                Tenant
              </label>
              <input
                id="tenant_slug"
                name="tenant_slug"
                type="text"
                autoComplete="off"
                className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg text-secondary-900 bg-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all sm:text-sm"
                placeholder="Il tuo tenant"
                value={formData.tenant_slug}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-1">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg text-secondary-900 bg-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all sm:text-sm"
                placeholder="mario.rossi@azienda.it"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-secondary-700">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  Password dimenticata?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="w-full px-4 py-2.5 pr-11 border border-secondary-300 rounded-lg text-secondary-900 bg-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all sm:text-sm"
                  placeholder="Inserisci la password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-secondary-400 hover:text-secondary-600 focus:outline-none focus:text-primary-600 transition-colors"
                  aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                  title={showPassword ? 'Nascondi password' : 'Mostra password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Accesso in corso...
                  </div>
                ) : (
                  'Accedi'
                )}
              </button>
            </div>
          </form>
        </div>

        <p className="mt-8 text-center text-sm text-secondary-600">
          Non hai un account?{' '}
          <Link to="/register" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
            Registrati qui
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;